/**
 * AIInsightSection Component
 * Reusable collapsible section that fires a pre-formed AI question
 * and shows the response inline. Drops into any tab.
 */

import { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { SectionCard } from './SectionCard';
import { CommentButton } from './CommentButton';
import { queryAI } from '@/api/aiApi';

interface AIInsightSectionProps {
  /** Unique identifier for this insight (used for comment targetId) */
  id: string;
  /** Section title */
  title?: string;
  /** Pre-formed question sent to the AI */
  question: string;
  /** Optional context hint for the AI (e.g. "cash", "compliance") */
  contextHint?: string;
}

function simpleMarkdown(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')
    .replace(/^/, '<p>').replace(/$/, '</p>');
}

export function AIInsightSection({
  id,
  title = 'AI Analysis',
  question,
  contextHint,
}: AIInsightSectionProps) {
  const [answer, setAnswer] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await queryAI(question, contextHint);
      setAnswer(response.answer);
      setDisclaimer(response.disclaimer);
      setLoaded(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get AI response';
      if (message.includes('not configured') || message.includes('unavailable')) {
        setError('AI insights not configured. Set ANTHROPIC_API_KEY to enable.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [question, contextHint, loading]);

  return (
    <SectionCard
      title={title}
      badge={
        loaded ? (
          <span onClick={(e) => e.stopPropagation()} role="presentation">
            <CommentButton targetType="page" targetId={`ai-${id}`} />
          </span>
        ) : undefined
      }
    >
      <div className="ai-insight-section">
        {!loaded && !loading && !error && (
          <button
            className="ai-insight-section__generate"
            onClick={handleGenerate}
            type="button"
          >
            Generate AI Analysis
          </button>
        )}
        {loading && (
          <div className="ai-insight-section__loading">
            <div className="loading-spinner" />
            <span>Analysing your financial data…</span>
          </div>
        )}
        {error && (
          <div className="ai-insight-section__error-block">
            <p className="ai-insight-section__error">{error}</p>
            <button
              className="ai-insight-section__generate"
              onClick={handleGenerate}
              disabled={loading}
              type="button"
            >
              Try Again
            </button>
          </div>
        )}
        {answer && (
          <>
            <div
              className="ai-insight-section__answer"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(simpleMarkdown(answer)) }}
            />
            {disclaimer && (
              <p className="ai-insight-section__disclaimer">{disclaimer}</p>
            )}
            <button
              className="ai-insight-section__refresh"
              onClick={handleGenerate}
              disabled={loading}
              type="button"
            >
              Refresh
            </button>
          </>
        )}
      </div>
    </SectionCard>
  );
}
